/**
 * Broker Login Step (Step 2)
 * Handles broker platform login via URL and monitors login status
 */

import React, { useState, useEffect } from "react";
import { Alert, Link } from "@mui/material";
import { Icon, Loader } from "../../../../components/ds";
import { services } from "../../../../services/api";
import LoadingSpinner from "../../Components/LoadingSpinner";
import AlertMessage from "../../Components/AlertMessage";

const BrokerLoginStep = ({
  accountData,
  selectedBroker,
  isEditMode,
  onLoginSuccess,
  onBack,
  loading: parentLoading,
  onStatusChange, // New prop to communicate status changes to parent
  // PRESENTATION ONLY. `compact` drops the chrome this component draws for the
  // add-account WIZARD (its own broker card, status badge, message alert and Back
  // button) and leaves just the action + its live states. In the connect dialog
  // that chrome was duplicated by the dialog around it — the broker was named five
  // times on one small surface — and the Back button was a third way to dismiss
  // next to Cancel and X. Every branch below is presentational; no state, no
  // fetch, no handler differs between the two modes, so the wizard (which passes
  // nothing) renders exactly as it does today.
  compact = false,
}) => {
  // Component state
  const [loading, setLoading] = useState(false);
  const [loginUrl, setLoginUrl] = useState("");
  const [loginStatus, setLoginStatus] = useState("pending"); // pending, in-progress, success, error
  const [loginMessage, setLoginMessage] = useState("");
  const [error, setError] = useState("");
  const [embedConfig, setEmbedConfig] = useState(null);
  const [loginInstructions, setLoginInstructions] = useState(null);
  const [additionalParams, setAdditionalParams] = useState(null);

  // Get login URL and config from backend
  const initializeLogin = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      console.log("Fetching login URL for account ID:", accountData.account_id);
      const response = await services.brokerAccounts.getLoginUrl(
        accountData.account_id,
        isEditMode
      );
      console.log("Login URL response:", response);
      console.log("Login URL responseData:", response.data.data);

      if (response.data.status === "success") {
        const responseData = response.data.data;

        // Set login URL and config
        setLoginUrl(responseData.login_url);
        setEmbedConfig(responseData.supports_iframe);
        setLoginMessage(
          responseData.message ||
            "Click the button below to login to your broker platform."
        );

        // Extract and set login instructions
        if (responseData.login_instructions) {
          setLoginInstructions(responseData.login_instructions);
        }

        // Extract and set additional parameters
        if (responseData.additional_params) {
          setAdditionalParams(responseData.additional_params);
        }

        // Notify parent component about the status and data change
        if (onStatusChange) {
          onStatusChange(
            "pending",
            responseData.login_instructions,
            responseData.additional_params
          );
        }
      } else {
        throw new Error(response.error?.message || "Failed to get login URL");
      }
    } catch (err) {
      setError(`Failed to initialize login: ${err.message}`);
      setLoginStatus("error");
      // Notify parent about error status
      if (onStatusChange) {
        onStatusChange("error");
      }
    } finally {
      setLoading(false);
    }
  }, [accountData, isEditMode, onStatusChange]);

  // Initialize login process
  useEffect(() => {
    console.log("accountData in BrokerLoginStep:", accountData);
    if (accountData?.account_id) {
      console.log(
        "Initializing login with account ID:",
        accountData.account_id
      );
      initializeLogin();
    } else {
      console.error("Missing accountData.account_id, cannot initialize login");
    }
  }, [accountData, initializeLogin]);

  // Listen for broker login status updates from localStorage/sessionStorage
  useEffect(() => {
    if (!accountData?.account_id) {
      console.log("❌ BrokerLoginStep: No account_id found in accountData:", accountData);
      return;
    }

    console.log("🎯 BrokerLoginStep: Setting up event listener for account:", accountData.account_id);

    // Helper to get status from storage
    const getStatusFromStorage = () => {
      let statusData = null;
      try {
        const local = localStorage.getItem("brokerLoginStatus");
        const session = sessionStorage.getItem("brokerLoginStatus");
        statusData = local ? JSON.parse(local) : session ? JSON.parse(session) : null;
        console.log("💾 Retrieved status from storage:", statusData);
      } catch (e) {
        console.error("Error parsing status from storage:", e);
        statusData = null;
      }
      if (
        statusData &&
        (statusData.brokerAccountId === accountData.account_id || statusData.broker_account_id === accountData.account_id)
      ) {
        console.log("✅ Account ID matches in storage:", {
          storageAccountId: statusData.brokerAccountId || statusData.broker_account_id,
          localAccountId: accountData.account_id
        });
        return statusData;
      } else if (statusData) {
        console.log("❌ Account ID mismatch in storage:", {
          storageAccountId: statusData.brokerAccountId || statusData.broker_account_id,
          localAccountId: accountData.account_id,
          statusData
        });
      }
      return null;
    };

    // Initial check on mount
    const statusData = getStatusFromStorage();
    if (statusData) {
      if (statusData.status === "completed" || statusData.success) {
        setLoginStatus("success");
        setLoginMessage("Login successful! Proceeding to validation...");
        setTimeout(() => {
          onLoginSuccess();
        }, 1200);
      } else if (statusData.status === "failed" || statusData.error) {
        setLoginStatus("error");
        setError(statusData.error || "Login failed. Please try again.");
      } else if (statusData.status === "processing" || statusData.status === "in-progress") {
        setLoginStatus("in-progress");
        setLoginMessage("Please complete the login process in the broker platform...");
      }
    }

    // The cross-tab CustomEvent listener that used to live here was removed. The
    // callback dispatches `brokerLoginStatusUpdate` in ITS OWN tab (login is opened with
    // window.open), and a window CustomEvent cannot cross tabs — so it never fired. The
    // mount read of localStorage above IS the real signal, because localStorage is
    // shared across tabs. (In the connect dialog, LoginModal now also detects success
    // over the WebSocket; this mount read remains the fallback for the wizard.)
  }, [accountData, onLoginSuccess]);

  // Start login process
  const handleStartLogin = () => {
    if (!loginUrl) {
      setError("Login URL not available. Please try refreshing.");
      return;
    }
    setLoginStatus("in-progress");
    setLoginMessage(
      "Please complete the login process in the broker platform..."
    );
    // Notify parent about status change
    if (onStatusChange) {
      onStatusChange("in-progress", loginInstructions, additionalParams);
    }
    // Open login URL
    if (embedConfig?.embed_in_page) {
      window.open(loginUrl, "_blank", "width=800,height=600");
    } else {
      window.open(loginUrl, "_blank");
    }
    // No polling, rely on callback + event
  };

  // Retry login process
  const handleRetry = () => {
    setLoginStatus("pending");
    setError("");
    setLoginMessage("");
    // Notify parent about retry
    if (onStatusChange) {
      onStatusChange("pending", loginInstructions, additionalParams);
    }
    initializeLogin();
  };

  // Render status indicator
  const renderStatusIndicator = () => {
    switch (loginStatus) {
      case "pending":
        return (
          <span className="ds-badge sm neutral">
            <Icon name="refresh" size={13} />
            Ready to Login
          </span>
        );
      case "in-progress":
        return (
          <span className="ds-badge sm info">
            <Icon name="external" size={13} />
            Login in Progress
          </span>
        );
      case "success":
        return (
          <span className="ds-badge sm profit">
            <Icon name="success" size={13} />
            Login Successful
          </span>
        );
      case "error":
        return (
          <span className="ds-badge sm loss">
            <Icon name="error" size={13} />
            Login Failed
          </span>
        );
      default:
        return null;
    }
  };

  // ---- compact: the connect dialog ---------------------------------------
  // ONE button, which never unmounts. Only its own contents change.
  //
  // The shared branch below swaps its ENTIRE body for <LoadingSpinner> while the
  // login URL is fetched — so the action disappeared and came back ("the button is
  // getting vanished and reoccurring") — and its in-progress state is an MUI
  // circle at size={60}, the "big circle icon". Neither belongs on a surface this
  // small: the only thing that changed is one button's own state, so that is the
  // only thing that should move.
  //
  // The DS already had this exact affordance; its docstring spells it out:
  //     <Loader size="sm" tone="inherit" />   // in a button: E-spark, white
  // tone="inherit" takes the button's own foreground, so the glyph reads as part
  // of the button rather than a spinner parked on top of it.
  //
  // Presentation only — handleStartLogin / handleRetry / the states they read are
  // the same ones the shared branch uses.
  if (compact) {
    const brokerLabel = selectedBroker?.display_name || selectedBroker?.name;
    const preparing = loading || parentLoading;
    const waiting = loginStatus === "in-progress";
    const done = loginStatus === "success";
    const failed = loginStatus === "error";
    const busy = preparing || waiting;

    const label = done
      ? `${brokerLabel} connected`
      : waiting
      ? `Waiting for ${brokerLabel}…`
      : preparing
      ? "Preparing…"
      : failed
      ? "Try again"
      : `Continue to ${brokerLabel}`;

    return (
      <div className="flex flex-col gap-3">
        {error && (
          <AlertMessage severity="error" title="Login Error" message={error} />
        )}
        <button
          type="button"
          className="btn btn-primary btn-lg w-full"
          disabled={busy || done || (!failed && !loginUrl)}
          onClick={failed ? handleRetry : handleStartLogin}
        >
          {busy ? (
            <Loader size="sm" tone="inherit" />
          ) : (
            <Icon
              name={done ? "success" : failed ? "refresh" : "external"}
              size={16}
            />
          )}
          {label}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Error Message */}
      {error && (
        <div className="mb-6">
          <AlertMessage severity="error" title="Login Error" message={error} />
        </div>
      )}

      {/* Loading State */}
      {loading || parentLoading ? (
        <LoadingSpinner message="Preparing login process..." />
      ) : (
        <div>
          {/* Broker Info Card */}
          <div className="bg-bg-3 dark:bg-bg-3 border border-[var(--divider)] rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-4">
              {selectedBroker?.logo && (
                <img
                  src={selectedBroker.logo}
                  alt={selectedBroker.name}
                  className="w-8 h-8"
                />
              )}
              <h3 className="ds-h3 text-fg-1">
                {selectedBroker?.display_name || selectedBroker?.name}
              </h3>
              {renderStatusIndicator()}
            </div>

            <p className="text-fg-2 text-sm">
              Account: {accountData?.account_name}
            </p>

            {loginMessage && (
              <Alert severity="info" className="mt-4">
                {loginMessage}
              </Alert>
            )}
          </div>

          {/* Login Action */}
          <div className="flex justify-center mb-6">
            {loginStatus === "pending" && loginUrl && (
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={handleStartLogin}
              >
                <Icon name="external" size={16} />
                Login to {selectedBroker?.display_name || selectedBroker?.name}
              </button>
            )}

            {loginStatus === "in-progress" && (
              <div className="text-center">
                <LoadingSpinner
                  message="Waiting for login completion..."
                  size={60}
                />
                <p className="text-fg-2 mt-4 text-sm">
                  Complete the login process in the broker window
                </p>
              </div>
            )}

            {loginStatus === "success" && (
              <div className="text-center">
                <Icon name="success" className="text-green-500 text-6xl mb-4" />
                <h3 className="ds-h3 text-green-600 dark:text-green-400">
                  Login Successful!
                </h3>
                <p className="text-fg-2 text-sm">
                  Proceeding to validation...
                </p>
              </div>
            )}

            {loginStatus === "error" && (
              <div className="text-center">
                <Icon name="error" className="text-red-500 text-6xl mb-4" />
                <h3 className="ds-h3 text-red-600 dark:text-red-400">
                  Login Failed
                </h3>
                <button
                  type="button"
                  className="btn btn-secondary mt-4"
                  onClick={handleRetry}
                >
                  Try Again
                </button>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
              disabled={loginStatus === "in-progress"}
            >
              Back
            </button>

            {/* The success case automatically proceeds, so no manual "Next" button needed */}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokerLoginStep;
